import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Gift, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gutscheine() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    axios.get(`${API}/vouchers/products`).then((r) => {
      if (ok) {
        setProducts(r.data);
        setLoading(false);
      }
    }).catch(() => setLoading(false));
    return () => { ok = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#F5EFE3]" data-testid="gutscheine-page">
      {/* Hero */}
      <section className="relative pt-24 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 border border-[#D68A64] rounded-full text-[#D68A64] text-xs tracking-[0.25em] uppercase">
            <Gift size={14} /> Gutscheine
          </div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#2C2A26] leading-[1.05] mb-6">
            Ein Abend,
            <br />
            <em className="text-[#D68A64] not-italic font-normal italic">zum Verschenken.</em>
          </h1>
          <p className="max-w-2xl mx-auto text-lg text-[#5C574E] font-light leading-relaxed">
            Statt Bluetooth-Speaker Nummer vier: ein Guthaben, das die Beschenkten
            in einen Menü-Abend zuhause einlösen — Menü, Personenzahl und Termin ganz nach Wunsch.
          </p>
          <p className="max-w-2xl mx-auto text-sm text-[#8A7A68] italic mt-4">
            Der Gutscheinbetrag ist ein Guthaben, das bei Einlösung von der Endrechnung
            abgezogen wird. Reicht es nicht, wird der Restbetrag beim Einsatz fällig.
          </p>

          <figure className="mt-14 max-w-2xl mx-auto" data-testid="gutschein-foto">
            <img src="/gutschein-foto.jpg" loading="lazy"
              alt="Gutschein von Lukas Stutz mit Wachssiegel, Umschlag und Kupferband"
              className="w-full shadow-[0_34px_80px_-40px_rgba(120,80,48,0.65)]" />
            <figcaption className="text-xs text-[#8A7A68] italic mt-4">
              Handschriftlich ausgefüllt, mit Wachssiegel versehen und im Umschlag per Post.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Bundles */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block px-6 py-2 border-y border-[#D68A64] text-[#D68A64] text-xs tracking-[0.3em] uppercase">
              Wählen Sie einen Anlass
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-[#D68A64]" size={32} />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="voucher-products">
              {products.map((p) => (
                <Link
                  key={p.product_type}
                  to={`/gutscheine/bestellen/${p.product_type}`}
                  data-testid={`voucher-card-${p.product_type}`}
                  className="group bg-[#FBF7EC] border border-[#E3DECE] p-8 hover:border-[#D68A64] transition-all hover:shadow-lg block"
                >
                  <div className="text-xs tracking-[0.25em] uppercase text-[#8A7A68] mb-3">
                    Gutschein
                  </div>
                  <h3 className="font-serif text-2xl text-[#2C2A26] mb-3 group-hover:text-[#D68A64] transition-colors">
                    {p.name}
                  </h3>
                  <p className="text-sm text-[#5C574E] leading-relaxed mb-6 min-h-[3rem]">
                    {p.subtitle}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-[#E3DECE]">
                    <span className="text-xs tracking-wider uppercase text-[#8A7A68]">
                      Guthaben frei wählbar
                    </span>
                    <ArrowRight size={18} className="text-[#D68A64] group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* So funktioniert's */}
      <section className="px-6 py-20 bg-[#FBF7EC] border-y border-[#E3DECE]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-block px-6 py-2 border-y border-[#D68A64] text-[#D68A64] text-xs tracking-[0.3em] uppercase mb-4">
              So kommt der Gutschein zu Ihnen
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2C2A26]">In drei Schritten.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { n: "01", t: "Sie bestellen", d: "Wählen Sie einen Anlass, das Guthaben (freier Betrag ab 50 €) und tragen Empfänger, Anlass und eine persönliche Widmung ein." },
              { n: "02", t: "Sie überweisen", d: "Sie erhalten eine Rechnung mit IBAN und Verwendungszweck. Nach Zahlungseingang geht es los." },
              { n: "03", t: "Der Brief kommt", d: "Ich fülle Ihren Gutschein handschriftlich aus, versiegle ihn mit einem Wachssiegel und schicke ihn per Post." },
            ].map((s) => (
              <div key={s.n} className="text-center">
                <div className="font-serif italic text-5xl text-[#D68A64] mb-4">{s.n}</div>
                <h4 className="font-serif text-xl text-[#2C2A26] mb-3">{s.t}</h4>
                <p className="text-sm text-[#5C574E] leading-relaxed font-light">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer note */}
      <section className="px-6 py-12 text-center">
        <p className="text-sm text-[#8A7A68] italic max-w-2xl mx-auto leading-relaxed">
          Der Gutscheinbetrag ist ein <b>Guthaben</b>, das bei Einlösung von der individuell
          kalkulierten Endrechnung des Menü-Einsatzes abgezogen wird. Alle Gutscheine sind
          3 Jahre gültig, frei übertragbar und keine Barauszahlung.
        </p>
      </section>
    </div>
  );
}
